import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, DollarSign, TrendingDown, Settings, CheckCircle, XCircle, Target, Activity, BarChart3, Zap, Clock } from 'lucide-react';
import UpgradePrompt from './components/UpgradePrompt';
import { hasActiveSubscription } from './subscriptionUtils';
import { getTrades, getRiskSettings, saveRiskSettings as saveRiskSettingsToFirestore, getWeeklyViolations } from './services/firestoreService';
import './RiskManager.css';

const RiskManager = ({ currentUser, onNavigate, capital: capitalProp, onCapitalChange }) => {
  const [capitalInput, setCapitalInput] = useState('');
  const [editingCapital, setEditingCapital] = useState(false);
  const [trades, setTrades] = useState([]);
  const [riskSettings, setRiskSettings] = useState({
    dailyLossLimit: 5000,
    weeklyLossLimit: 15000,
    monthlyLossLimit: 50000,
    maxPositionSize: 50000,
    maxOpenTrades: 3,
    maxTradesPerDay: 5,
    riskPerTrade: 2,
    maxConsecutiveLosses: 3,
    maxSingleTradeLoss: 0
  });
  const [weeklyViolations, setWeeklyViolations] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [currentRisk, setCurrentRisk] = useState({
    dailyLoss: 0,
    weeklyLoss: 0,
    monthlyLoss: 0,
    todayTrades: 0,
    openTrades: 0
  });
  const [positionCalculator, setPositionCalculator] = useState({
    capital: 100000,
    entry: '',
    stopLoss: '',
    riskPercent: 2,
    direction: 'LONG'
  });
  const [calculatedPosition, setCalculatedPosition] = useState(null);
  const [isPaid, setIsPaid] = useState(null); // null = loading

  // Get week number — defined before useEffect so the effect closure can call it
  const getWeekNumber = (date) => {
    const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const dayNum = d.getUTCDay() || 7;
    d.setUTCDate(d.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
    return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  };

  // Calculate current risk levels — defined before useEffect so the effect closure can call it
  const calculateCurrentRisk = (tradesData) => {
    const today = new Date().toISOString().split('T')[0];
    const currentWeek = getWeekNumber(new Date());
    const currentMonth = new Date().getMonth();
    const currentYear = new Date().getFullYear();

    let dailyLoss = 0;
    let weeklyLoss = 0;
    let monthlyLoss = 0;
    let todayTrades = 0;

    tradesData.forEach(trade => {
      const tradeDate = new Date(trade.date);
      const tradeWeek = getWeekNumber(tradeDate);
      const tradeMonth = tradeDate.getMonth();
      const tradeYear = tradeDate.getFullYear();

      // Daily loss
      if (trade.date === today && trade.pnl < 0) {
        dailyLoss += Math.abs(trade.pnl);
      }

      // Today's trade count
      if (trade.date === today) {
        todayTrades++;
      }

      // Weekly loss
      if (tradeWeek === currentWeek && tradeYear === currentYear && trade.pnl < 0) {
        weeklyLoss += Math.abs(trade.pnl);
      }

      // Monthly loss
      if (tradeMonth === currentMonth && tradeYear === currentYear && trade.pnl < 0) {
        monthlyLoss += Math.abs(trade.pnl);
      }
    });

    console.log('[RiskManager] Risk calculated — dailyLoss:', dailyLoss, 'weeklyLoss:', weeklyLoss, 'monthlyLoss:', monthlyLoss, 'todayTrades:', todayTrades);
    setCurrentRisk({
      dailyLoss,
      weeklyLoss,
      monthlyLoss,
      todayTrades,
      openTrades: 0 // This would come from live positions in real implementation
    });
  };

  // Load user's trades and risk settings from Firestore
  useEffect(() => {
    // Clear stale data immediately on any user change
    setTrades([]);
    setCurrentRisk({ dailyLoss: 0, weeklyLoss: 0, monthlyLoss: 0, todayTrades: 0, openTrades: 0 });
    setIsPaid(null);

    if (currentUser) {
      console.log('[RiskManager] Loading for user:', currentUser.id);
      const load = async () => {
        const paid = await hasActiveSubscription(currentUser.id);
        setIsPaid(paid);
        if (paid) {
          const [userTrades, savedSettings, violations] = await Promise.all([
            getTrades(currentUser.id),
            getRiskSettings(currentUser.id),
            getWeeklyViolations(currentUser.id)
          ]);
          console.log('[RiskManager] Fetched', userTrades.length, 'trades for user:', currentUser.id);
          setTrades(userTrades);
          if (savedSettings) {
            setRiskSettings(prev => ({ ...prev, ...savedSettings }));
          }
          setWeeklyViolations(violations);
          calculateCurrentRisk(userTrades);
        }
      };
      load();
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  // Gate: loading state
  if (isPaid === null) return null;

  // Gate: require active subscription
  if (!isPaid) {
    return (
      <UpgradePrompt
        requiredPlan="pro"
        feature="Risk Manager Pro plan mein available hai. Daily/weekly loss limits aur position sizing set karo."
      />
    );
  }

  // Save risk settings to Firestore
  const saveRiskSettings = async () => {
    await saveRiskSettingsToFirestore(currentUser.id, riskSettings);
    setShowSettings(false);
    alert('Risk settings saved successfully! 🛡️');
  };

  // Handle settings change
  const handleSettingsChange = (e) => {
    const { name, value } = e.target;
    setRiskSettings({
      ...riskSettings,
      [name]: parseFloat(value) || 0
    });
  };

  // Calculate position size — handles both LONG and SHORT
  const calculatePosition = () => {
    const entry = parseFloat(positionCalculator.entry);
    const stopLoss = parseFloat(positionCalculator.stopLoss);
    const capital = parseFloat(positionCalculator.capital);
    const riskPercent = parseFloat(positionCalculator.riskPercent);
    const direction = positionCalculator.direction;

    if (!entry || !stopLoss || !capital || !riskPercent) {
      alert('Please fill all fields');
      return;
    }

    if (direction === 'LONG' && stopLoss >= entry) {
      alert('For LONG trades, Stop Loss must be below Entry Price.\nExample: Entry ₹100, Stop Loss ₹95');
      return;
    }

    if (direction === 'SHORT' && stopLoss <= entry) {
      alert('For SHORT trades, Stop Loss must be above Entry Price.\nExample: Entry ₹100, Stop Loss ₹105');
      return;
    }

    const riskAmount = (capital * riskPercent) / 100;
    // LONG: risk per share = entry - stopLoss
    // SHORT: risk per share = stopLoss - entry (SL is above entry for shorts)
    const riskPerShare = direction === 'LONG' ? (entry - stopLoss) : (stopLoss - entry);
    const quantity = Math.floor(riskAmount / riskPerShare);
    const positionValue = quantity * entry;

    setCalculatedPosition({
      quantity,
      riskAmount,
      riskPerShare,
      positionValue,
      maxLoss: riskAmount,
      direction
    });
  };

  // Get risk status color for loss bars (thresholds: 50 / 80 / 100 %)
  const getRiskColor = (current, limit) => {
    const percentage = (current / limit) * 100;
    if (percentage >= 100) return { bgStyle: { background: 'var(--danger-bg)', border: '1px solid var(--danger)' }, textColor: 'var(--danger)', barClass: 'rm-progress-fill rm-progress-danger' };
    if (percentage >= 80)  return { bgStyle: { background: 'var(--danger-bg)', border: '1px solid var(--danger)' }, textColor: 'var(--danger)', barClass: 'rm-progress-fill rm-progress-danger' };
    if (percentage >= 50)  return { bgStyle: { background: 'var(--warning-bg)', border: '1px solid var(--warning)' }, textColor: 'var(--warning)', barClass: 'rm-progress-fill rm-progress-warning' };
    return { bgStyle: { background: 'var(--success-bg)', border: '1px solid var(--profit)' }, textColor: 'var(--profit)', barClass: 'rm-progress-fill rm-progress-safe' };
  };

  // Get risk status color for trades bar (thresholds: 60 / 80 / 100 %)
  const getTradesColor = (current, limit) => {
    const percentage = (current / limit) * 100;
    if (percentage >= 100) return { bgStyle: { background: 'var(--danger-bg)', border: '1px solid var(--danger)' }, textColor: 'var(--danger)', barClass: 'rm-progress-fill rm-progress-danger' };
    if (percentage >= 80)  return { bgStyle: { background: 'var(--danger-bg)', border: '1px solid var(--danger)' }, textColor: 'var(--danger)', barClass: 'rm-progress-fill rm-progress-danger' };
    if (percentage >= 60)  return { bgStyle: { background: 'var(--warning-bg)', border: '1px solid var(--warning)' }, textColor: 'var(--warning)', barClass: 'rm-progress-fill rm-progress-warning' };
    return { bgStyle: { background: 'var(--success-bg)', border: '1px solid var(--profit)' }, textColor: 'var(--profit)', barClass: 'rm-progress-fill rm-progress-safe' };
  };

  // Calculate risk percentage
  const calculateRiskPercentage = (current, limit) => {
    return Math.min(((current / limit) * 100).toFixed(1), 100);
  };

  // Check if trading is allowed
  const isTradingAllowed = () => {
    const checks = [];

    // Daily loss check
    if (currentRisk.dailyLoss >= riskSettings.dailyLossLimit) {
      checks.push({
        rule: 'Daily Loss Limit',
        status: 'blocked',
        message: `Daily loss limit reached: ₹${currentRisk.dailyLoss.toFixed(0)} / ₹${riskSettings.dailyLossLimit}`
      });
    }

    // Weekly loss check
    if (currentRisk.weeklyLoss >= riskSettings.weeklyLossLimit) {
      checks.push({
        rule: 'Weekly Loss Limit',
        status: 'blocked',
        message: `Weekly loss limit reached: ₹${currentRisk.weeklyLoss.toFixed(0)} / ₹${riskSettings.weeklyLossLimit}`
      });
    }

    // Monthly loss check
    if (currentRisk.monthlyLoss >= riskSettings.monthlyLossLimit) {
      checks.push({
        rule: 'Monthly Loss Limit',
        status: 'blocked',
        message: `Monthly loss limit reached: ₹${currentRisk.monthlyLoss.toFixed(0)} / ₹${riskSettings.monthlyLossLimit}`
      });
    }

    // Daily trades check
    if (currentRisk.todayTrades >= riskSettings.maxTradesPerDay) {
      checks.push({
        rule: 'Max Trades Per Day',
        status: 'blocked',
        message: `Maximum trades per day reached: ${currentRisk.todayTrades} / ${riskSettings.maxTradesPerDay}`
      });
    }

    return {
      allowed: checks.length === 0,
      violations: checks
    };
  };

  const tradingStatus = isTradingAllowed();

  return (
    <div className="rm-page space-y-6">
      {/* Header */}
      <div className="rm-card animate-fade-in-up" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 44, height: 44, borderRadius: 10, background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Shield size={22} style={{ color: '#000' }} />
          </div>
          <div>
            <h2 className="rm-page-title">Risk Manager</h2>
            <p className="rm-page-subtitle" style={{ marginBottom: 0 }}>Protect Your Capital with Smart Risk Management</p>
          </div>
        </div>
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="rm-btn-outline"
        >
          <Settings size={16} />
          <span>Settings</span>
        </button>
      </div>

      {/* Trading Status Alert */}
      {!tradingStatus.allowed && (
        <div className="rm-status-danger animate-fade-in-up" style={{ borderLeft: '4px solid var(--danger)' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
            <XCircle style={{ color: 'var(--danger)', flexShrink: 0, marginTop: 2 }} size={22} />
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--danger)', marginBottom: 6, letterSpacing: '-0.02em' }}>Trading Blocked!</h3>
              <p style={{ fontSize: 13, color: 'var(--danger)', marginBottom: 10 }}>आपकी risk limits exceed हो गई हैं। Trading temporarily blocked है।</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tradingStatus.violations.map((violation, idx) => (
                  <div key={idx} className="rm-violation-item">
                    <p style={{ fontWeight: 600, color: 'var(--danger)', fontSize: 13 }}>{violation.rule}</p>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{violation.message}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {tradingStatus.allowed && (
        <div className="rm-status-safe animate-fade-in-up" style={{ borderLeft: '4px solid var(--profit)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <CheckCircle style={{ color: 'var(--profit)', flexShrink: 0 }} size={20} />
            <div>
              <p style={{ fontWeight: 700, color: 'var(--profit)', fontSize: 13 }}>Trading Allowed</p>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>All risk parameters within limits. Safe to trade!</p>
            </div>
          </div>
        </div>
      )}

      {/* Risk Settings Panel */}
      {showSettings && (
        <div className="rm-card animate-fade-in-up">
          <p className="rm-section-label">Configure Risk Settings</p>

          {/* Trading Capital */}
          <div style={{ marginBottom: 20, padding: 16, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 10 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 8 }}>
              <DollarSign size={15} style={{ color: 'var(--accent)' }} />
              <span>Trading Capital (₹)</span>
            </label>
            {editingCapital ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="number"
                  value={capitalInput}
                  onChange={(e) => setCapitalInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const val = parseFloat(capitalInput) || 100000;
                      onCapitalChange && onCapitalChange(val);
                      setPositionCalculator(prev => ({ ...prev, capital: val }));
                      setEditingCapital(false);
                    }
                  }}
                  className="rm-input"
                  style={{ width: 160 }}
                  autoFocus
                />
                <button
                  onClick={() => {
                    const val = parseFloat(capitalInput) || 100000;
                    onCapitalChange && onCapitalChange(val);
                    setPositionCalculator(prev => ({ ...prev, capital: val }));
                    setEditingCapital(false);
                  }}
                  className="rm-btn-primary"
                  style={{ padding: '8px 14px', fontSize: 13 }}
                >Save</button>
                <button onClick={() => setEditingCapital(false)} className="rm-btn-outline" style={{ padding: '7px 12px', fontSize: 13 }}>Cancel</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>₹{(capitalProp || 100000).toLocaleString()}</span>
                <button
                  onClick={() => { setCapitalInput((capitalProp || 100000).toString()); setEditingCapital(true); }}
                  className="rm-btn-outline"
                  style={{ padding: '4px 10px', fontSize: 12 }}
                >Edit</button>
              </div>
            )}
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>Used for P&L % calculation across all reports</p>
          </div>

          <div className="rm-grid-2">
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Daily Loss Limit (₹)
              </label>
              <input
                type="number"
                name="dailyLossLimit"
                value={riskSettings.dailyLossLimit}
                onChange={handleSettingsChange}
                className="rm-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Weekly Loss Limit (₹)
              </label>
              <input
                type="number"
                name="weeklyLossLimit"
                value={riskSettings.weeklyLossLimit}
                onChange={handleSettingsChange}
                className="rm-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Monthly Loss Limit (₹)
              </label>
              <input
                type="number"
                name="monthlyLossLimit"
                value={riskSettings.monthlyLossLimit}
                onChange={handleSettingsChange}
                className="rm-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Max Position Size (₹)
              </label>
              <input
                type="number"
                name="maxPositionSize"
                value={riskSettings.maxPositionSize}
                onChange={handleSettingsChange}
                className="rm-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Max Trades Per Day
              </label>
              <input
                type="number"
                name="maxTradesPerDay"
                value={riskSettings.maxTradesPerDay}
                onChange={handleSettingsChange}
                className="rm-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Risk Per Trade (%)
              </label>
              <input
                type="number"
                name="riskPerTrade"
                value={riskSettings.riskPerTrade}
                onChange={handleSettingsChange}
                step="0.5"
                className="rm-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Max Consecutive Losses
                <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--text-muted)' }}>(0 = disabled)</span>
              </label>
              <input
                type="number"
                name="maxConsecutiveLosses"
                value={riskSettings.maxConsecutiveLosses}
                onChange={handleSettingsChange}
                min="0"
                className="rm-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
                Max Single Trade Loss (₹)
                <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--text-muted)' }}>(0 = disabled)</span>
              </label>
              <input
                type="number"
                name="maxSingleTradeLoss"
                value={riskSettings.maxSingleTradeLoss}
                onChange={handleSettingsChange}
                min="0"
                className="rm-input"
              />
            </div>
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 12 }}>
            <button
              onClick={saveRiskSettings}
              className="rm-btn-primary"
              style={{ flex: 1, justifyContent: 'center' }}
            >
              Save Settings
            </button>
            <button
              onClick={() => setShowSettings(false)}
              className="rm-btn-outline"
              style={{ flex: 1, justifyContent: 'center' }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Risk Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Daily Loss */}
        {(() => {
          const color = getRiskColor(currentRisk.dailyLoss, riskSettings.dailyLossLimit);
          return (
            <div className="rm-card animate-fade-in-up" style={{ ...color.bgStyle, borderLeft: `3px solid ${color.textColor}`, marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Daily Loss</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: color.textColor }} className="animate-pulse" />
                  <TrendingDown size={16} style={{ color: color.textColor }} />
                </div>
              </div>
              <p style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8, letterSpacing: '-0.02em' }}>₹{currentRisk.dailyLoss.toFixed(0)}</p>
              <div className="rm-progress-track" style={{ marginBottom: 8 }}>
                <div className={color.barClass} style={{ width: `${calculateRiskPercentage(currentRisk.dailyLoss, riskSettings.dailyLossLimit)}%` }} />
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{calculateRiskPercentage(currentRisk.dailyLoss, riskSettings.dailyLossLimit)}% of ₹{riskSettings.dailyLossLimit}</p>
            </div>
          );
        })()}

        {/* Weekly Loss */}
        {(() => {
          const color = getRiskColor(currentRisk.weeklyLoss, riskSettings.weeklyLossLimit);
          return (
            <div className="rm-card animate-fade-in-up" style={{ ...color.bgStyle, borderLeft: `3px solid ${color.textColor}`, marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Weekly Loss</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: color.textColor }} className="animate-pulse" />
                  <Activity size={16} style={{ color: color.textColor }} />
                </div>
              </div>
              <p style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8, letterSpacing: '-0.02em' }}>₹{currentRisk.weeklyLoss.toFixed(0)}</p>
              <div className="rm-progress-track" style={{ marginBottom: 8 }}>
                <div className={color.barClass} style={{ width: `${calculateRiskPercentage(currentRisk.weeklyLoss, riskSettings.weeklyLossLimit)}%` }} />
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{calculateRiskPercentage(currentRisk.weeklyLoss, riskSettings.weeklyLossLimit)}% of ₹{riskSettings.weeklyLossLimit}</p>
            </div>
          );
        })()}

        {/* Monthly Loss */}
        {(() => {
          const color = getRiskColor(currentRisk.monthlyLoss, riskSettings.monthlyLossLimit);
          return (
            <div className="rm-card animate-fade-in-up" style={{ ...color.bgStyle, borderLeft: `3px solid ${color.textColor}`, marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Monthly Loss</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: color.textColor }} className="animate-pulse" />
                  <BarChart3 size={16} style={{ color: color.textColor }} />
                </div>
              </div>
              <p style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8, letterSpacing: '-0.02em' }}>₹{currentRisk.monthlyLoss.toFixed(0)}</p>
              <div className="rm-progress-track" style={{ marginBottom: 8 }}>
                <div className={color.barClass} style={{ width: `${calculateRiskPercentage(currentRisk.monthlyLoss, riskSettings.monthlyLossLimit)}%` }} />
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{calculateRiskPercentage(currentRisk.monthlyLoss, riskSettings.monthlyLossLimit)}% of ₹{riskSettings.monthlyLossLimit}</p>
            </div>
          );
        })()}

        {/* Today's Trades */}
        {(() => {
          const color = getTradesColor(currentRisk.todayTrades, riskSettings.maxTradesPerDay);
          return (
            <div className="rm-card animate-fade-in-up" style={{ ...color.bgStyle, borderLeft: `3px solid ${color.textColor}`, marginBottom: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-secondary)' }}>Today's Trades</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: color.textColor }} className="animate-pulse" />
                  <Zap size={16} style={{ color: color.textColor }} />
                </div>
              </div>
              <p style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8, letterSpacing: '-0.02em' }}>{currentRisk.todayTrades}</p>
              <div className="rm-progress-track" style={{ marginBottom: 8 }}>
                <div className={color.barClass} style={{ width: `${calculateRiskPercentage(currentRisk.todayTrades, riskSettings.maxTradesPerDay)}%` }} />
              </div>
              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{calculateRiskPercentage(currentRisk.todayTrades, riskSettings.maxTradesPerDay)}% of {riskSettings.maxTradesPerDay} max</p>
            </div>
          );
        })()}
      </div>

      {/* Position Size Calculator */}
      <div className="rm-card animate-fade-in-up">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
          <Target style={{ color: 'var(--accent)' }} />
          <p className="rm-section-label" style={{ marginBottom: 0 }}>Position Size Calculator</p>
        </div>

        {/* Direction toggle */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
          <button
            onClick={() => { setPositionCalculator({ ...positionCalculator, direction: 'LONG' }); setCalculatedPosition(null); }}
            style={{
              flex: 1,
              padding: '8px 0',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              border: positionCalculator.direction === 'LONG' ? 'none' : '1px solid var(--border)',
              background: positionCalculator.direction === 'LONG' ? 'var(--profit)' : 'var(--bg-primary)',
              color: positionCalculator.direction === 'LONG' ? '#000' : 'var(--text-muted)',
              transition: 'all 0.15s'
            }}
          >
            📈 LONG (Buy)
          </button>
          <button
            onClick={() => { setPositionCalculator({ ...positionCalculator, direction: 'SHORT' }); setCalculatedPosition(null); }}
            style={{
              flex: 1,
              padding: '8px 0',
              borderRadius: 8,
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              border: positionCalculator.direction === 'SHORT' ? 'none' : '1px solid var(--border)',
              background: positionCalculator.direction === 'SHORT' ? 'var(--danger)' : 'var(--bg-primary)',
              color: positionCalculator.direction === 'SHORT' ? '#fff' : 'var(--text-muted)',
              transition: 'all 0.15s'
            }}
          >
            📉 SHORT (Sell)
          </button>
        </div>

        {/* Direction hint */}
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          {positionCalculator.direction === 'LONG'
            ? '📌 LONG: Stop Loss must be below Entry Price (e.g., Entry ₹100 → SL ₹95)'
            : '📌 SHORT: Stop Loss must be above Entry Price (e.g., Entry ₹100 → SL ₹105)'}
        </p>

        <div className="rm-grid-2" style={{ marginBottom: 14 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Total Capital (₹)
            </label>
            <input
              type="number"
              value={positionCalculator.capital}
              onChange={(e) => setPositionCalculator({ ...positionCalculator, capital: e.target.value })}
              className="rm-input"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Risk Per Trade (%)
            </label>
            <input
              type="number"
              value={positionCalculator.riskPercent}
              onChange={(e) => setPositionCalculator({ ...positionCalculator, riskPercent: e.target.value })}
              step="0.5"
              className="rm-input"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Entry Price (₹)
            </label>
            <input
              type="number"
              value={positionCalculator.entry}
              onChange={(e) => setPositionCalculator({ ...positionCalculator, entry: e.target.value })}
              step="0.01"
              className="rm-input"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 6 }}>
              Stop Loss (₹)
              <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                {positionCalculator.direction === 'LONG' ? '(below entry)' : '(above entry)'}
              </span>
            </label>
            <input
              type="number"
              value={positionCalculator.stopLoss}
              onChange={(e) => setPositionCalculator({ ...positionCalculator, stopLoss: e.target.value })}
              step="0.01"
              className="rm-input"
            />
          </div>
        </div>
        <button
          onClick={calculatePosition}
          className="rm-btn-primary"
          style={{
            width: '100%',
            justifyContent: 'center',
            padding: '11px 20px',
            background: positionCalculator.direction === 'SHORT' ? 'var(--danger)' : 'var(--accent)',
            color: positionCalculator.direction === 'SHORT' ? '#fff' : '#000'
          }}
        >
          Calculate {positionCalculator.direction} Position Size
        </button>

        {calculatedPosition && (
          <div style={{
            marginTop: 14,
            padding: 16,
            borderRadius: 10,
            border: `1px solid ${calculatedPosition.direction === 'SHORT' ? 'var(--danger)' : 'var(--accent)'}`,
            background: calculatedPosition.direction === 'SHORT' ? 'var(--danger-bg)' : 'var(--accent-subtle)'
          }}>
            <h4 style={{ fontWeight: 700, marginBottom: 12, color: calculatedPosition.direction === 'SHORT' ? 'var(--danger)' : 'var(--accent)' }}>
              {calculatedPosition.direction === 'SHORT' ? '📉 SHORT' : '📈 LONG'} Position Result:
            </h4>
            <div className="rm-grid-2" style={{ fontSize: 13 }}>
              <div>
                <p style={{ color: 'var(--text-muted)' }}>Quantity:</p>
                <p style={{ fontSize: 20, fontWeight: 700, color: calculatedPosition.direction === 'SHORT' ? 'var(--danger)' : 'var(--accent)' }}>
                  {calculatedPosition.quantity} shares
                </p>
              </div>
              <div>
                <p style={{ color: 'var(--text-muted)' }}>Position Value:</p>
                <p style={{ fontSize: 20, fontWeight: 700, color: calculatedPosition.direction === 'SHORT' ? 'var(--danger)' : 'var(--accent)' }}>
                  ₹{calculatedPosition.positionValue.toFixed(0)}
                </p>
              </div>
              <div>
                <p style={{ color: 'var(--text-muted)' }}>Risk Per Share:</p>
                <p style={{ fontWeight: 700, color: 'var(--text-primary)' }}>₹{calculatedPosition.riskPerShare.toFixed(2)}</p>
              </div>
              <div>
                <p style={{ color: 'var(--text-muted)' }}>Max Loss:</p>
                <p style={{ fontWeight: 700, color: 'var(--loss)' }}>₹{calculatedPosition.maxLoss.toFixed(0)}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Weekly Rule Violations */}
      <div className="rm-card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Clock size={20} style={{ color: 'var(--warning)' }} />
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)' }}>Rule Violations This Week</h3>
          </div>
          <span style={{
            padding: '3px 12px',
            borderRadius: 100,
            fontSize: 13,
            fontWeight: 700,
            background: weeklyViolations.length === 0 ? 'var(--success-bg)' : 'var(--danger-bg)',
            color: weeklyViolations.length === 0 ? 'var(--profit)' : 'var(--danger)',
            border: `1px solid ${weeklyViolations.length === 0 ? 'var(--profit)' : 'var(--danger)'}`
          }}>
            {weeklyViolations.length} violation{weeklyViolations.length !== 1 ? 's' : ''}
          </span>
        </div>

        {weeklyViolations.length === 0 ? (
          <div className="rm-status-safe" style={{ marginBottom: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <CheckCircle size={20} style={{ color: 'var(--profit)', flexShrink: 0 }} />
            <p style={{ fontSize: 13, color: 'var(--profit)' }}>No rule violations this week. Great discipline! 🎯</p>
          </div>
        ) : (
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            {weeklyViolations.slice().reverse().map((v, i) => (
              <div key={v.id || i} className="rm-violation-item" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: 'var(--danger)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>⚠️ {v.title}</p>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.message}</p>
                </div>
                <span style={{
                  marginLeft: 8,
                  flexShrink: 0,
                  fontSize: 11,
                  padding: '2px 8px',
                  borderRadius: 100,
                  fontWeight: 500,
                  background: v.action === 'overridden' ? 'var(--warning-bg)' : 'var(--accent-subtle)',
                  color: v.action === 'overridden' ? 'var(--warning)' : 'var(--accent)'
                }}>
                  {v.action === 'overridden' ? 'Overridden' : 'Stopped'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Risk Management Tips */}
      <div className="rm-card">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <Shield size={22} style={{ color: 'var(--profit)' }} />
          <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-primary)' }}>Risk Management Best Practices</h3>
        </div>
        <div className="rm-grid-2">
          <div style={{ padding: 16, borderRadius: 10, background: 'var(--bg-primary)', border: '1px solid var(--profit)' }}>
            <h4 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--profit)' }}>✅ Golden Rules:</h4>
            <ul style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, listStyle: 'none', padding: 0, margin: 0 }}>
              <li>• Never risk more than 2% per trade</li>
              <li>• Always use stop loss</li>
              <li>• Don't overtrade - quality over quantity</li>
              <li>• Follow your limits strictly</li>
            </ul>
          </div>
          <div style={{ padding: 16, borderRadius: 10, background: 'var(--bg-primary)', border: '1px solid var(--danger)' }}>
            <h4 style={{ fontWeight: 700, marginBottom: 8, color: 'var(--danger)' }}>❌ Avoid These:</h4>
            <ul style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8, listStyle: 'none', padding: 0, margin: 0 }}>
              <li>• Trading after hitting daily limit</li>
              <li>• Revenge trading after losses</li>
              <li>• Moving stop loss to avoid exit</li>
              <li>• Ignoring risk rules</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskManager;
